/**
 * 인증 세대와 스냅샷 — 설계 v7 §4-3.
 *
 * 이 모듈이 있는 이유는 하나다. 느린 응답이 도착했을 때 **그 사이에 계정이 바뀌었는지**를
 * 알아야 한다. React 상태는 리렌더 시점에 묶여 있어 답이 되지 못하므로, 반영 직전에 저장소를
 * 다시 읽어 요청 당시의 스냅샷과 맞춘다. 하나라도 다르면 그 응답은 남의 것이다.
 *
 * 토큰 기록·삭제는 전부 여기를 지난다(ESLint 규칙이 강제). 두 저장소에 토큰이 동시에 남으면
 * 어느 쪽이 현재 계정인지 알 수 없으므로, 쓰는 쪽이 반대쪽을 비우는 것을 계약으로 둔다.
 */

const TOKEN_KEY = 'token';
const EPOCH_KEY = 'loginhs:auth-epoch';

/** 저장소 접근은 사생활 모드·차단 설정에서 던질 수 있다. 던지면 없는 것으로 본다. */
function safeGet(store, key) {
  try {
    return store ? store.getItem(key) : null;
  } catch (error) {
    return null;
  }
}

function safeSet(store, key, value) {
  try {
    if (store) store.setItem(key, value);
  } catch (error) {
    /* 저장 실패는 기능을 멈추지 않는다 */
  }
}

function safeRemove(store, key) {
  try {
    if (store) store.removeItem(key);
  } catch (error) {
    /* 위와 같다 */
  }
}

/** base64url JWT payload 디코드. **서명을 검증하지 않는다** — 비교용이다. */
function decodePayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '==='.slice((base64.length + 3) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(json);
  } catch (error) {
    return null;
  }
}

export function readToken() {
  return safeGet(window.localStorage, TOKEN_KEY) || safeGet(window.sessionStorage, TOKEN_KEY);
}

/**
 * 지금 저장소에 있는 토큰의 신원. 토큰이 없거나 디코드가 실패하면 null.
 *
 * userId 는 JWT `sub` 다 — 서버 JwtService.parse 가 getSubject() 를 userId 로 쓰므로 같은 매핑이다.
 */
export function getAuthSnapshot() {
  const token = readToken();
  if (!token) return null;
  const payload = decodePayload(token);
  if (!payload) return null;
  return {
    token,
    userId: payload.sub == null ? null : String(payload.sub),
    groupId: payload.groupId == null ? null : String(payload.groupId),
    jti: payload.jti == null ? null : String(payload.jti),
  };
}

function readEpoch() {
  const raw = safeGet(window.sessionStorage, EPOCH_KEY);
  const value = Number.parseInt(raw, 10);
  return Number.isFinite(value) ? value : 0;
}

let epoch = typeof window !== 'undefined' ? readEpoch() : 0;
const listeners = new Set();

export function authEpoch() {
  return epoch;
}

function bumpEpoch() {
  epoch += 1;
  safeSet(window.sessionStorage, EPOCH_KEY, String(epoch));
  listeners.forEach((listener) => {
    try {
      listener(epoch);
    } catch (error) {
      /* 구독자 하나가 던져도 나머지에 알린다 */
    }
  });
}

/** 계정이 바뀔 때 알림을 받는다. 카드가 진행 중 요청을 끊고 상태를 비우는 데 쓴다. */
export function onAuthChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * 토큰 기록. 지정한 저장소에 쓰고 **반대쪽은 비운다**.
 * @param {'local'|'session'} store
 */
export function setToken(token, store = 'local') {
  if (store === 'session') {
    safeSet(window.sessionStorage, TOKEN_KEY, token);
    safeRemove(window.localStorage, TOKEN_KEY);
  } else {
    safeSet(window.localStorage, TOKEN_KEY, token);
    safeRemove(window.sessionStorage, TOKEN_KEY);
  }
  bumpEpoch();
}

/** 로그아웃. 두 저장소를 모두 비운다. */
export function clearToken() {
  safeRemove(window.localStorage, TOKEN_KEY);
  safeRemove(window.sessionStorage, TOKEN_KEY);
  bumpEpoch();
}

/** 다른 탭에서 토큰이 바뀌어도 세대가 오른다 — 그 탭의 로그아웃이 이 탭에도 닿아야 한다. */
if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('storage', (event) => {
    if (event && event.key === TOKEN_KEY) bumpEpoch();
  });
}

/** 시험에서 세대를 되돌리기 위한 것. 생산 코드는 쓰지 않는다. */
export function __resetForTest() {
  epoch = 0;
  safeRemove(window.sessionStorage, EPOCH_KEY);
  listeners.clear();
}
